import { CalendarShift, Job } from '@core/app/types/logworkApiType'
import { DayjsType } from '@page/logwork/logwork'
import { LogWorkTabType } from '@page/logwork/types/types'
import dayjs from 'dayjs'

type DatesType = {
    start: DayjsType
    end: DayjsType
} | null

export const formatArrayForJobSelect = (
    arr: Job[],
    dates: DatesType,
    isHeadSelect: boolean = false,
    shifts: CalendarShift[],
    id: number | null = null,
    tabType: LogWorkTabType
) => {
    return arr.map((item) => {
        const isOpenedForDays = Array.from({ length: 7 }).map((_, dayIndex) => {
            return !isDayDisabled(
                dayIndex,
                item,
                { start: dayjs(dates?.start), end: dayjs(dates?.end) },
                shifts,
                tabType
            )
        })

        const availableTabs = (['hours', 'manualHours', 'piecework', 'addition'] as LogWorkTabType[]).filter((tab) => {
            return item.settings[tab as keyof Job['settings']]
        })

        const isDisabled = (() => {
            if (isHeadSelect) {
                return disabledAllWeek(item, { start: dayjs(dates?.start), end: dayjs(dates?.end) }, shifts, tabType)
            } else {
                return (
                    id !== null &&
                    isDayDisabled(id, item, { start: dayjs(dates?.start), end: dayjs(dates?.end) }, shifts, tabType)
                )
            }
        })()

        return {
            value: item.jobId.toString(),
            label: item.name,
            sublabel: `Period: ${dayjs(item.fromDate).format('DD MMM YYYY')} - ${item.toDate === '2100-01-01' ? 'Ongoing' : dayjs(item.toDate).format('DD MMM YYYY')}`,
            disabled: isDisabled,
            disabledWarning: isHeadSelect
                ? getNAtext(item, { start: dayjs(dates?.start), end: dayjs(dates?.end) }, shifts, tabType) || null
                : null,
            isOpenedForDays,
            availableTabs,
        }
    })
}

export const isDayDisabled = (
    day: string | number | null,
    job: Job,
    dates: DatesType,
    shifts: CalendarShift[],
    tabType: LogWorkTabType,
    jobsInDay: Job[] = []
): boolean => {
    // if (tabType !== 'hours' && tabType !== 'addition') return false
    if (!dates) return true
    if (!job && jobsInDay.length > 0 && tabType === 'hours') return false

    const dayIndex = typeof day === 'string' && day.includes('-') ? parseInt(day.split('-')[0], 10) : Number(day)

    const { start } = dates
    const { settings, fromDate, toDate, hourRules } = job
    const currentDate = dayjs().startOf('day')
    const targetDate = start.add(dayIndex, 'days').startOf('day')

    if (targetDate.isAfter(toDate)) return true

    const hasShift = shifts.some((shift) => dayjs(shift.date).isSame(targetDate, 'day'))
    if (hasShift) return false

    const isOutsideCalendar = !settings.logOutsideCalendar
    const isBeforeJobStart = targetDate.isBefore(fromDate)
    const isRestrictedByHours =
        tabType === 'hours' || tabType === 'manualHours'
            ? hourRules.length > 0 && hourRules.includes(dayIndex + 1)
            : true
    const isTooFarInFuture = settings.logFuture
        ? targetDate.diff(currentDate, 'month') > 6
        : targetDate.isAfter(currentDate)
    const isBeforeClosingDate = settings.closingDate 
        ? targetDate.isBefore(dayjs(settings.closingDate).startOf('day'))
        : false
    return isOutsideCalendar || isBeforeJobStart || !isRestrictedByHours || isTooFarInFuture || isBeforeClosingDate
}

const disabledAllWeek = (job: Job, dates: DatesType, shifts: CalendarShift[], tabType: LogWorkTabType) => {
    for (let day = 0; day < 7; day++) {
        if (!isDayDisabled(day, job, dates, shifts, tabType)) return false
    }
    return true
}

const getNAtext = (job: Job, dates: DatesType, shifts: CalendarShift[], tabType: LogWorkTabType) => {
    const { hourRules } = job
    const unavailableDates: DayjsType[] = []

    if (!dates || hourRules.length === 0) return null

    for (let day = 0; day < 7; day++) {
        isDayDisabled(day, job, dates, shifts, tabType)
            ? unavailableDates.push(dayjs(dates.start).add(day, 'days'))
            : null
    }

    if (unavailableDates.length === 0) return null

    const firstDate = unavailableDates[0]
    const lastDate = unavailableDates[unavailableDates.length - 1]

    if (firstDate.isSame(lastDate, 'month')) {
        return `N/A for ${firstDate.format('DD')} - ${lastDate.format('DD MMM')}`
    } else {
        return `N/A for ${firstDate.format('DD MMM')} - ${lastDate.format('DD MMM')}`
    }
}
